import { ArticleOutlined, ContentCopy, DeleteOutline, Link } from "@mui/icons-material";
import {
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import moment from "moment";
import { AttachmentImage } from "../../components";
import { PublishedIcon } from "../../images/svg/publishedIcon";
import { WIPIcon } from "../../images/svg/WIPIcon";
import { CapitalizeString, GiveMeStatusColor } from "../../utils";
import { SourcingOption } from "../viewMaterial/sourcing";

export const getSourceDetail = (row: any) => {
  return [
    SourcingOption.find((s) => s.value === row?.sourcing_type)?.label || "",
    ...Object.entries(row?.vendor || {}).map(([key, value]) => {
      if (key === "vendor_id") {
        return "";
      } else {
        return value;
      }
    }),
    ...Object.values(row?.organization_unit || {}),
  ]
    .filter(Boolean)
    .join("; ");
};

export const DashboardTableColumn = (arg: {
  onDelete?: (row: any) => void,
  openRevision?: (row: any) => void,
  view?: (row: any) => void,
  openAssociation?: (row: any) => void,
  showTechnicalCard?: (row: any) => void,
  updatePublishStatus?: (row: any) => void,
  disableNavigation?: boolean,
  removeDelete?: boolean,
  publishable?: boolean,
  getParentPath?: (id: string) => string;
  handleDuplicate?: (row: any) => void;
}) => {

  const {
    onDelete = () => false,
    view = () => false,
    openAssociation = () => false,
    showTechnicalCard = () => false,
    updatePublishStatus = () => false,
    disableNavigation = false,
    removeDelete = false,
    publishable = false,
    getParentPath = () => "",
    handleDuplicate = () => { },
  } = arg;

  let columns: GridColDef[] = [
    {
      field: "sequence_id",
      headerName: "Material ID",
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        if (disableNavigation) {
          return (
            <Typography variant="body2">{params.row?.sequence_id}</Typography>
          );
        }
        return (
          <Typography
            id="view-material-icon"
            component={"a"}
            href={getParentPath(params.row?._id)}
            sx={{
              color: "blue",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            variant="body2"
            onClick={(e) => {
              view(params.row);
              e.preventDefault();
            }}
          >
            {params.row?.sequence_id}
          </Typography>
        );
      },
    },
    {
      field: "version",
      headerName: "Revision",
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        return <Typography variant="body2" id="material-version">{params.row?.version}</Typography>;
      },
    },
    {
      field: "duplicate",
      headerName: "Duplicate",
      resizable: true,
      width: 90,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        return (
          <Tooltip title="Make a Duplicate">
            <IconButton
              sx={{ margin: "auto" }}
              onClick={() =>
                handleDuplicate(params?.row)
              }
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "released",
      headerName: "Status",
      width: 90,
      renderCell: (params: GridRenderCellParams) => {
        return (
          params?.row?.released ? (
            <Button disableRipple disableFocusRipple sx={{ cursor: "default", '&:hover': { backgroundColor: 'transparent' } }}>
              <Tooltip
                title="Published"
                children={<div><PublishedIcon /></div>}
              />
            </Button>
          ) : (
            <Button disableRipple disableFocusRipple id="publish-material">
              <Tooltip
                title={publishable ? "Work in progress, click to publish the item" : "Not Published"}
                children={
                  <div><WIPIcon onClick={() => publishable ? updatePublishStatus(params.row) : false} /></div>
                }
              />
            </Button>
          )
        );
      },
    },
    {
      field: "attachments",
      headerName: "Image",
      width: 70,
      renderCell: (params: GridRenderCellParams) => <AttachmentImage object_id={params.row._id} object_type="material" {...(params?.row?.attachments?.at(-1) ?? {})} />,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "short_description",
      headerName: "Description",
      width: 300,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        return (
          <Tooltip title={params.row?.short_description ?? params.row?.long_description}>
            <Typography
              variant="body2"
              sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {params.row?.short_description ?? params.row?.long_description}
            </Typography>
          </Tooltip>
        );
      },
    },
    { field: "external_ref_id", headerName: "Ref. No.", width: 150 },
    { field: "unit_region_name", headerName: "Region", width: 150 },
    {
      field: "group_name",
      headerName: "Groups",
      width: 250,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        return (
          <Stack
            spacing={1}
            direction={"row"}
            width="100%"
            id={"tagsChip"}
          >
            <Chip size={"small"} label={params.row?.group_name ?? ""} />
          </Stack>
        );
      },
    },
    {
      field: "noun_name",
      headerName: "Noun",
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        <Stack
          spacing={1}
          direction={"row"}
          width="100%"
          id={"tagsChip"}
        >
          <Chip size={"small"} label={params.row?.noun_name ?? ""} />
        </Stack>;
      },
    },
    // {
    //   field: 'priority', headerName: 'Priority', width: 150,
    //   valueGetter: (value, row, column, apiRef) => { return row.tags_info?.filter((_: any) => _.tag_group_canonical_name === TAG_CANONICAL_NAMES.material_priority)?.[0]?.tag_name ?? "" },
    //   renderCell: (params: GridRenderCellParams) => {
    //     if (params.rowNode.type === 'group') {
    //       if (params.rowNode.groupingField === 'status') {
    //         return params.rowNode.groupingKey;
    //       }
    //       return '';
    //     }
    //     return (
    //       <Stack
    //         spacing={1}
    //         direction={"row"}
    //         id={"tagsChip"}
    //         sx={{
    //           width: "100%",
    //         }}>
    //         {params.row.tags_info?.filter((_: any) => _.tag_group_canonical_name === TAG_CANONICAL_NAMES.material_priority).map((tag: any) => <Chip variant="outlined" sx={{ borderColor: tag.tag_group_color, backgroundColor: tag.tag_group_color, color: `${hexToGrayscale(tag.tag_group_color) > 150 ? "rgba(0, 0, 0, 0.87)" : "#FFFFFF"}` }} size={"small"} label={tag?.tag_name ?? ""} />)}
    //       </Stack>
    //     );
    //   },
    //   groupingValueGetter: (value, row, column, apiRef) => {
    //     return row.tags_info?.filter((_: any) => _.tag_group_canonical_name === TAG_CANONICAL_NAMES.material_priority)?.[0]?.tag_name ?? "";
    //   },
    // },
    {
      field: "sourcing_type",
      headerName: "Sourcing Details",
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        return (
          <Typography variant="body2">{getSourceDetail(params?.row)}</Typography>
        );
      },
      valueGetter: (value, row, column, apiRef) => getSourceDetail(row),
    },
    { field: "cost_category", headerName: "Costing Type", width: 150 },
    {
      field: "money.cost",
      headerName: "Cost",
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        return (
          <Typography variant="body2">{params?.row?.money?.cost}</Typography>
        );
      },
      valueGetter: (value, row, column, apiRef) => row?.money?.cost,
    },
    {
      field: "money.currency",
      headerName: "Currency",
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Typography variant="body2">{params?.row?.money?.currency}</Typography>
        );
      },
      valueGetter: (value, row, column, apiRef) => row?.money?.currency,
    },
    { field: "lead_time_days", headerName: "Lead Time (Weeks)", width: 150 },
    {
      field: "link",
      headerName: "Dependencies",
      width: 140,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        return (
          <IconButton
            onClick={() => openAssociation(params.row)}
          >
            <Link
              color={"info"}
            />
          </IconButton>
        );
      },
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "technical_card",
      headerName: "Technical Card",
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        return (
          <>
            <IconButton onClick={() => showTechnicalCard(params.row)}>
              <ArticleOutlined color={"info"} />
            </IconButton>
          </>
        );
      },
      disableColumnMenu: true,
      sortable: false,
    },
    // {
    //     field: 'attachement_tags', headerName: 'Attachment Tags', width: 250, renderCell: (params: GridRenderCellParams) => (
    //         <Stack spacing={1} direction={"row"} width="100%" overflow={"scroll"} id={"tagsChip"}>
    //             {params.row?.attachement_tags?.map((tag: any, key: number) => (<Chip key={key} variant="outlined" size={"small"} label={`${tag.tag_name}`} />))}
    //         </Stack>)
    // },
    { field: "created_user", headerName: "Created By", width: 150 },
    {
      field: "created_time",
      headerName: "Created Date",
      width: 250,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        return (
          <Typography variant="body2">
            {moment(params.row.created_time).format("lll")}
          </Typography>
        );
      },
    },
    { field: "modified_user", headerName: "Last modified by", width: 150 },
    {
      field: "modified_time",
      headerName: "Last modified date",
      width: 250,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        return (
          <Typography variant="body2">
            {params.row.modified_time
              ? moment(params.row.modified_time).format("lll")
              : ""}
          </Typography>
        );
      },
    },
    { field: 'released_user', headerName: 'Published by', width: 150 },
    {
      field: 'released_time', headerName: 'Published date', width: 250, renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === 'group') {
          return params.value;
        }
        return <Typography variant="body2">{params.row.released_time ? moment(params.row.released_time).format('lll') : ""}</Typography>
      }
    },
    {
      field: "status",
      headerName: "Work Status",
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") {
          return params.value;
        }
        return (
          <Typography
            variant="body2"
            color={GiveMeStatusColor(params?.row?.status)}
          >
            {CapitalizeString(params.row.status)}
          </Typography>
        );
      },
    },
    {
      field: "delete",
      headerName: "Delete",
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title={params?.row?.released ? `${params?.row?.sequence_id} Revision is published` : `Delete the Material ${params?.row?.sequence_id}`}>
            <IconButton disableRipple={params?.row?.released} onClick={() => params?.row?.released ? () => { } : onDelete(params.row)}>
              <DeleteOutline color={params?.row?.released ? "disabled" : "error"} id="delete-icon" />
            </IconButton>
          </Tooltip>
        );
      },
      disableColumnMenu: true,
      sortable: false,
    }
  ];

  if (removeDelete) {
    columns.pop();
  }

  return columns
};
